// src/components/programs/ProgramForm/Step1Sections/DayItineraryList.jsx

import React from "react";
import { Box, Typography, Alert } from "@mui/material";
import DayCard from "./DayItinerary/DayCard";

/**
 * Day Itinerary List Component
 * Displays all days with their products
 */
const DayItineraryList = ({
    formData,
    setFormData,
    errors,
    isArabic,
    activeLang,
    hotels,
}) => {
    /**
     * Handle add product to day (from Drag & Drop or Button)
     */
    const handleAddProduct = (dayIndex, productInstance) => {
        const newItinerary = [...formData.itinerary];
        const day = newItinerary[dayIndex];
        
        // Add the product instance to the day
        day.products = [...day.products, productInstance];
        
        // Update form data
        setFormData({ ...formData, itinerary: newItinerary });
    };

    /**
     * Handle remove product from day
     */
    const handleRemoveProduct = (dayIndex, instanceId) => {
        const newItinerary = [...formData.itinerary];
        const day = newItinerary[dayIndex];

        // Remove product and reorder
        day.products = day.products
            .filter((p) => p.instance_id !== instanceId)
            .map((p, index) => ({ ...p, order: index + 1 }));

        setFormData({ ...formData, itinerary: newItinerary });
    };

    /**
     * Handle reorder products within day
     */
    const handleReorderProducts = (dayIndex, newProductsOrder) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex].products = newProductsOrder.map((p, index) => ({
            ...p,
            order: index + 1,
        }));
        setFormData({ ...formData, itinerary: newItinerary });
    };

    /**
     * Handle update day name
     */
    const handleUpdateDayName = (dayIndex, lang, value) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex].day_name = {
            ...newItinerary[dayIndex].day_name,
            [lang]: value,
        };
        setFormData({ ...formData, itinerary: newItinerary });
    };

    /**
     * Handle update product dynamic field
     */
    const handleUpdateProductField = (dayIndex, instanceId, field, value) => {
        const newItinerary = [...formData.itinerary];
        const day = newItinerary[dayIndex];
        const productIndex = day.products.findIndex((p) => p.instance_id === instanceId);

        if (productIndex === -1) return;

        const product = { ...day.products[productIndex] };
        const keys = field.split(".");
        let current = product;

        for (let i = 0; i < keys.length - 1; i++) {
            current[keys[i]] = { ...current[keys[i]] };
            current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = value;
        day.products[productIndex] = product;

        setFormData({ ...formData, itinerary: newItinerary });
    };

    /**
     * Handle update day extras
     */
    const handleUpdateDayExtras = (dayIndex, extras) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex] = { ...newItinerary[dayIndex], extras };
        setFormData({ ...formData, itinerary: newItinerary });
    };

    if (!formData.itinerary || formData.itinerary.length === 0) {
        return (
            <Alert severity="warning">
                {isArabic
                    ? 'لا توجد أيام في البرنامج. الرجاء تحديد تواريخ البداية والنهاية في الخطوة السابقة.'
                    : 'No days in the program. Please set start and end dates in the previous step.'}
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>
                {isArabic ? '📅 برنامج الأيام' : '📅 Days Itinerary'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {formData.itinerary.map((day, index) => (
                    <DayCard
                        key={index}
                        day={day}
                        dayIndex={index}
                        onAddProduct={handleAddProduct}
                        onRemoveProduct={handleRemoveProduct}
                        onReorderProducts={handleReorderProducts}
                        onUpdateDayName={handleUpdateDayName}
                        onUpdateProductField={handleUpdateProductField}
                        onUpdateDayExtras={handleUpdateDayExtras}
                        hotels={hotels}
                        errors={errors}
                        isArabic={isArabic}
                        activeLang={activeLang}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default DayItineraryList;
